/**
 * Hook for tracking the public sales chat conversation.
 * - Persists a `visitor_token` in localStorage (anon visitor id).
 * - Sends visitor + assistant messages to /api/sales/messages so managers see
 *   the full conversation in their Sales tab.
 * - Resilient: if the backend / table isn't ready, every call no-ops silently
 *   so the UX never breaks.
 */
import { useCallback, useEffect, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = 'ruido_sales_visitor_v1';
const CONV_KEY = 'ruido_sales_conv_v1';

function ensureVisitorToken() {
  try {
    let t = localStorage.getItem(VISITOR_KEY);
    if (!t) {
      t = (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `vis-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
      localStorage.setItem(VISITOR_KEY, t);
    }
    return t;
  } catch {
    return `vis-${Date.now()}`;
  }
}

function getStoredConversationId() {
  try { return localStorage.getItem(CONV_KEY) || null; } catch { return null; }
}

function setStoredConversationId(id) {
  try {
    if (id) localStorage.setItem(CONV_KEY, id);
    else localStorage.removeItem(CONV_KEY);
  } catch { /* noop */ }
}

// XHR JSON helper — fetch is monkey-patched by the platform script and breaks
// non-2xx body reads.
function xhrJson(method, url, body, headers = {}) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = 10000;
    xhr.onload = () => {
      let data = {};
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
      catch { data = {}; }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: {} });
    xhr.ontimeout = () => resolve({ ok: false, status: 0, data: {} });
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

export function useSalesTracker(getAccessToken) {
  const conversationIdRef = useRef(getStoredConversationId());
  const visitorTokenRef = useRef(ensureVisitorToken());

  const trackMessage = useCallback(async ({ sender_type, content, message_type = 'text', metadata = {}, intent } = {}) => {
    if (!content && message_type === 'text') return;
    const token = typeof getAccessToken === 'function' ? getAccessToken() : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const { ok, data } = await xhrJson('POST', `${API}/api/sales/messages`, {
      conversation_id: conversationIdRef.current,
      visitor_token: visitorTokenRef.current,
      sender_type,
      content: content || '',
      message_type,
      metadata,
      intent,
    }, headers);
    if (ok && data?.conversation_id && data.conversation_id !== conversationIdRef.current) {
      conversationIdRef.current = data.conversation_id;
      setStoredConversationId(data.conversation_id);
    }
  }, [getAccessToken]);

  const linkSignup = useCallback(async (token) => {
    const visitor = visitorTokenRef.current;
    if (!visitor || !token) return;
    await xhrJson('POST', `${API}/api/sales/link-user`, { visitor_token: visitor }, {
      Authorization: `Bearer ${token}`,
    });
    // After conversion, drop the conv reference so a fresh chat starts later
    setStoredConversationId(null);
    conversationIdRef.current = null;
  }, []);

  return { trackMessage, linkSignup, visitorToken: visitorTokenRef.current };
}
